import { useEffect, useRef, useState } from "react";

import { recordingAsWav } from "./audio-file";

/** Where the recorder is: idle, recording, or making the file after the stop. */
export type RecorderState = "idle" | "recording" | "processing";

export interface Recorder {
  state: RecorderState;
  /** Whole seconds recorded so far, live's ticking timer. */
  seconds: number;
  /** Why the microphone gave nothing (live says nothing; ours says why). */
  problem: string | null;
  start: () => Promise<void>;
  stop: () => void;
}

/** Ours, where live only logs to the console (SA3 difference 1). */
const NO_MICROPHONE = "The microphone is not available. Allow it in the browser and try again.";
/** Ours, when the take could not be made a file (SA3 difference 2). */
const UNREADABLE = "The recording could not be read. Try again.";

/**
 * Live's recorder (`useAudioRecorder`): the microphone into a MediaRecorder, a timer that ticks
 * each second, and on stop the recording as a WAV file for `onRecorded`. Like live's, it neither
 * stops at 60 seconds nor refuses a short take: the file's check says so after the stop.
 */
export function useRecorder(onRecorded: (file: File) => void): Recorder {
  const [state, setState] = useState<RecorderState>("idle");
  const [seconds, setSeconds] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const handler = useRef(onRecorded);
  useEffect(() => {
    handler.current = onRecorded;
  });
  // Leaving the panel mid-take stops the microphone.
  useEffect(
    () => () => {
      if (timer.current !== null) clearInterval(timer.current);
      const running = recorder.current;
      if (running !== null && running.state !== "inactive") {
        running.ondataavailable = null;
        running.onstop = null;
        running.stop();
      }
      running?.stream.getTracks().forEach((track) => track.stop());
    },
    [],
  );

  const start = async () => {
    setProblem(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setProblem(NO_MICROPHONE);
      return;
    }
    const chunks: Blob[] = [];
    const media = new MediaRecorder(stream);
    media.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    media.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      void recordingAsWav(new Blob(chunks, { type: media.mimeType }))
        .then((file) => handler.current(file))
        .catch(() => setProblem(UNREADABLE))
        .finally(() => setState("idle"));
    };
    recorder.current = media;
    // No timeslice: the whole take arrives in one `dataavailable`, at the stop.
    media.start();
    setSeconds(0);
    setState("recording");
    timer.current = setInterval(() => setSeconds((current) => current + 1), 1000);
  };

  const stop = () => {
    if (timer.current !== null) clearInterval(timer.current);
    timer.current = null;
    if (recorder.current?.state === "recording") {
      setState("processing");
      recorder.current.stop();
    }
  };

  return { state, seconds, problem, start, stop };
}
